import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CustomBreadcrumb, CustomInput, CustomButton, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal, ImportSummaryModal, ImportPreviewModal } from '@/shared/components/molecules';
import CustomModal from '@/shared/components/atoms/CustomModal/CustomModal';
import { Home, Users, Pencil, Trash2, Mail, UserPlus, Shield, User, LayoutGrid, Key, Eye, EyeOff, Upload, Download, X, Phone, Search } from 'lucide-react';
import { employeeApi } from '../api/employeeApi';
import { departmentApi } from '../api/departmentApi';
import { Employee, Department } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';
import env from '@/core/config/env';

const editEmployeeSchema = z.object({
    email: z.string().email('Invalid email address'),
    name: z.string().min(2, 'Name must be at least 2 characters'),
    department_ids: z.array(z.string()).min(1, 'At least one department is required'),
    phone_number: z.string()
        .min(1, 'Phone number is required')
        .regex(/^[+]?[0-9]{10,15}$/, 'Phone number must be between 10 and 15 digits'),
    designation: z.string().min(1, 'Designation is required'),
});

type EditEmployeeFormData = z.infer<typeof editEmployeeSchema>;

export const ListEmployees = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);

    // Search and Sort states
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
    const [selectedFilterDepartmentId, setSelectedFilterDepartmentId] = useState<string>('');

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    // Import status modal state
    const [isImportSummaryOpen, setIsImportSummaryOpen] = useState(false);
    const [importSummaryData, setImportSummaryData] = useState<{
        success: number;
        failed: number;
        errors: string[];
        fileName: string;
    } | null>(null);

    // Edit state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [employeeToEdit, setEmployeeToEdit] = useState<Employee | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const importFileInputRef = useRef<HTMLInputElement>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [isImportPreviewOpen, setIsImportPreviewOpen] = useState(false);
    const { showToast } = useToastStore();

    const schoolSlug = user?.school?.slug || '';

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm<EditEmployeeFormData>({
        resolver: zodResolver(editEmployeeSchema),
        defaultValues: {
            department_ids: [],
            phone_number: '',
            designation: ''
        }
    });

    useEffect(() => {
        if (schoolSlug) {
            fetchDepartments();
        }
    }, [schoolSlug]);

    const fetchEmployees = useCallback(async () => {
        if (!schoolSlug) return;
        setIsLoading(true);
        try {
            const response = await employeeApi.getEmployees(schoolSlug, { 
                page, 
                page_size: pageSize,
                department_id: selectedFilterDepartmentId || undefined,
                search: debouncedSearch.trim() || undefined,
                sort_by: sortBy,
                sort_order: sortOrder
            });
            if (response.success && response.data) {
                const data = response.data as any;
                if (Array.isArray(data)) {
                    setEmployees(data);
                    setTotalCount(data.length);
                    setTotalPages(1);
                    setHasNext(false);
                    setHasPrevious(false);
                } else {
                    setEmployees(data.items || []);
                    setTotalCount(data.total_count || 0);
                    setTotalPages(data.total_pages || 1);
                    setHasNext(data.has_next || false);
                    setHasPrevious(data.has_previous || false);
                }
            }
        } catch (error) {
            showToast('Failed to load employees', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [schoolSlug, page, pageSize, selectedFilterDepartmentId, debouncedSearch, sortBy, sortOrder, showToast]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const handleDownloadTemplate = async () => {
        if (!schoolSlug) return;
        setIsDownloading(true);
        try {
            const response = await employeeApi.downloadTemplate(schoolSlug, 'employees');
            if (response.success && response.data) {
                const contentDisposition = response.headers?.['content-disposition'];
                let fileName = 'employees_template.xlsx';
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename=(.+)/);
                    if (match && match[1]) {
                        fileName = match[1].replace(/['"]/g, '');
                    }
                }
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                showToast('Template downloaded successfully', 'success');
            } else {
                showToast(response.error || 'Failed to download template', 'error');
            }
        } catch (error) {
            showToast('Failed to download template', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !schoolSlug) return;

        const allowedExtensions = ['xlsx', 'xls', 'csv'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        
        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
            showToast('Please upload only Excel (.xlsx, .xls) or CSV (.csv) files.', 'error');
            if (importFileInputRef.current) {
                importFileInputRef.current.value = '';
            }
            return;
        }

        setImportFile(file);
        setIsImportPreviewOpen(true);
        if (importFileInputRef.current) {
            importFileInputRef.current.value = '';
        }
    };

    const fetchDepartments = async () => {
        const response = await departmentApi.getDepartments(schoolSlug, { page: 1, page_size: 50 });
        if (response.success && response.data) {
            const data = response.data as any;
            setDepartments(Array.isArray(data) ? data : (data.items || []));
        }
    };

    const handleDepartmentFilterChange = (val: string | number) => {
        setSelectedFilterDepartmentId(val as string);
        setPage(1);
    };

    const handleEditClick = (employee: Employee) => {
        setEmployeeToEdit(employee);

        const baseUrl = env.API_URL.replace(/\/api$/, '');
        const currentPhoto = employee.photo_url
            ? (employee.photo_url.startsWith('http') ? employee.photo_url : `${baseUrl}${employee.photo_url}`)
            : null;

        setPhotoPreview(currentPhoto);
        setPhoto(null);

        reset({
            name: employee.name || '',
            email: employee.email || '',
            department_ids: employee.departments?.map(d => d.id) || [],
            designation: employee.designation || '',
            phone_number: employee.phone_number || ''
        });

        setIsEditModalOpen(true);
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setPhoto(null);
        setPhotoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleUpdateSubmit = async (data: EditEmployeeFormData) => {
        if (!schoolSlug || !employeeToEdit) return;
        setIsSubmitting(true);

        const payload: any = {
            name: data.name,
            email: data.email,
            department_ids: data.department_ids,
            phone_number: data.phone_number,
            designation: data.designation
        };
        if (photo !== null) {
            payload.photo = photo;
        } else if (!photoPreview && employeeToEdit.photo_url) {
            payload.photo = null;
        }

        const response = await employeeApi.updateEmployee(schoolSlug, employeeToEdit.id, payload);

        if (response.success) {
            showToast('Employee updated successfully', 'success');
            await fetchEmployees();
            setIsEditModalOpen(false);
            setEmployeeToEdit(null);
        } else {
            showToast(response.error || 'Failed to update employee', 'error');
        }
        setIsSubmitting(false);
    };

    const handleDeleteClick = (employee: Employee) => {
        setEmployeeToDelete(employee);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (employeeToDelete && schoolSlug) {
            setIsDeleting(true);
            const response = await employeeApi.deleteEmployee(schoolSlug, employeeToDelete.id);
            if (response.success) {
                showToast('Employee deleted successfully', 'success');
                if (employees.length === 1 && page > 1) {
                    setPage(prev => prev - 1);
                } else {
                    fetchEmployees();
                }
            } else {
                showToast(response.error || 'Failed to delete employee', 'error');
            }
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
            setEmployeeToDelete(null);
        }
    };

    const columns: CustomTableColumn<Employee>[] = [
        {
            header: 'Employee Name',
            key: 'name',
            sortable: true,
            minWidth: '220px',
            render: (emp) => {
                const baseUrl = env.API_URL.replace(/\/api$/, '');
                const photoSrc = emp.photo_url
                    ? (emp.photo_url.startsWith('http') ? emp.photo_url : `${baseUrl}${emp.photo_url}`)
                    : null;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                        {photoSrc ? (
                            <img
                                src={photoSrc}
                                alt={emp.name || 'Employee'}
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    minWidth: '36px',
                                    minHeight: '36px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '1px solid var(--border-color)',
                                    flexShrink: 0
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '36px',
                                height: '36px',
                                minWidth: '36px',
                                minHeight: '36px',
                                borderRadius: '50%',
                                background: 'var(--accent-primary-light, rgba(99, 102, 241, 0.1))',
                                color: 'var(--accent-primary, #4f46e5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                flexShrink: 0
                            }}>
                                {(emp.name || 'U').charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: '600', color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                                {emp.name || 'Unknown User'}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                                {emp.email || emp.user_id}
                                {emp.phone_number ? ` • ${emp.phone_number}` : ''}
                            </div>
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Departments',
            key: 'departments',
            minWidth: '180px',
            render: (emp) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {emp.departments && emp.departments.length > 0 ? (
                        emp.departments.map(dept => (
                            <span
                                key={dept.id}
                                style={{
                                    padding: '0.25rem 0.5rem',
                                    background: 'var(--bg-secondary)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                {dept.name}
                            </span>
                        ))
                    ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>No departments</span>
                    )}
                </div>
            )
        },
        {
            header: 'Designation',
            key: 'designation',
            sortable: true,
            minWidth: '130px',
            render: (emp) => (
                emp.designation ? (
                    <span style={{
                        display: 'inline-block',
                        fontSize: '0.75rem',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        color: 'var(--text-secondary)',
                        fontWeight: '600'
                    }}>
                        {emp.designation}
                    </span>
                ) : (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>—</span>
                )
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '100px',
            width: '100px',
            render: (emp) => (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleEditClick(emp); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'all 0.2s' }}
                        title="Edit Employee"
                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-tertiary, rgba(0,0,0,0.05))'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                    >
                        <Pencil size={18} />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(emp); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', color: 'var(--status-error, #ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'all 0.2s' }}
                        title="Revoke Access"
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Employees', icon: <Users size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title="Employee Management"
                subtitle="View and manage staff members for your school."
                icon={<Users size={24} />}
                actionLabel="Add Employee"
                onActionClick={() => navigate(`/${schoolSlug}/school-admin/employees/invite`)}
            />

            {/* Responsive Action Bar (Search / Filter / Import / Download) */}
            <div className="table-filter-bar">
                {/* Left: Search Box & Department Filter */}
                <div className="table-filter-group">
                    <div className="table-search-box">
                        <Search size={16} className="table-search-icon" />
                        <input
                            type="text"
                            placeholder="Search by employee name, email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button
                                className="table-search-clear"
                                onClick={() => setSearch('')}
                                title="Clear search"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <div className="table-select-box">
                        <CustomSearchableSelect
                            placeholder="Filter by Department"
                            options={[
                                { label: 'All Departments', value: '' },
                                ...departments.map(d => ({ label: d.name, value: d.id }))
                            ]}
                            value={selectedFilterDepartmentId}
                            onChange={handleDepartmentFilterChange}
                            hideAvatar
                        />
                    </div>
                </div>

                {/* Right: Import / Download Actions */}
                <div className="table-filter-actions">
                    <CustomButton
                        variant="outline"
                        size="small"
                        onClick={handleDownloadTemplate}
                        disabled={isDownloading}
                    >
                        <Download size={16} style={{ marginRight: '0.5rem' }} />
                        {isDownloading ? 'Downloading...' : 'Download Template'}
                    </CustomButton>
                    
                    <CustomButton
                        variant="outline"
                        size="small"
                        onClick={() => importFileInputRef.current?.click()}
                        disabled={isImporting}
                    >
                        <Upload size={16} style={{ marginRight: '0.5rem' }} />
                        {isImporting ? 'Importing...' : 'Import Employees'}
                    </CustomButton>
                    <input
                        type="file"
                        ref={importFileInputRef}
                        onChange={handleFileImport}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            <CustomTable
                columns={columns}
                data={employees}
                isLoading={isLoading}
                isCompact={true}
                minWidth="680px"
                emptyMessage={debouncedSearch ? `No employees matching "${debouncedSearch}"` : "No employees added yet"}
                emptyIcon={<UserPlus size={32} />}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(colKey, order) => {
                    setSortBy(colKey);
                    setSortOrder(order);
                    setPage(1);
                }}
                pagination={{
                    currentPage: page,
                    totalPages: totalPages,
                    totalCount: totalCount,
                    pageSize: pageSize,
                    onPageChange: setPage,
                    hasPrevious: hasPrevious,
                    hasNext: hasNext,
                    isLoading: isLoading,
                    itemName: 'employees',
                }}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Employee"
                message={`Are you sure you want to revoke access and delete "${employeeToDelete?.name || 'this employee'}"? This action cannot be undone.`}
                confirmLabel={isDeleting ? "Deleting..." : "Delete"}
                variant="danger"
                isLoading={isDeleting}
            />

            {/* Edit Employee Modal */}
            <CustomModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEmployeeToEdit(null);
                    reset();
                }}
                title="Edit Employee"
                size="large"
            >
                <form onSubmit={handleSubmit(handleUpdateSubmit)} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    {/* Photo Upload */}
                    <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>Profile Photo (Optional)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            {photoPreview ? (
                                <div style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', border: '2px solid var(--border-color)' }}>
                                    <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    <button type="button" onClick={removePhoto} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                        <X size={12} />
                                    </button>
                                </div>
                            ) : (
                                <div onClick={() => fileInputRef.current?.click()} style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed var(--border-color)', cursor: 'pointer', color: 'var(--text-muted)' }}>
                                    <Upload size={24} />
                                </div>
                            )}
                            <div>
                                <CustomButton type="button" variant="outline" size="small" onClick={() => fileInputRef.current?.click()}>
                                    Upload Photo
                                </CustomButton>
                                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>JPG, PNG, GIF max 5MB</p>
                            </div>
                            <input type="file" ref={fileInputRef} onChange={handlePhotoChange} accept="image/*" style={{ display: 'none' }} />
                        </div>
                    </div>                    <div style={{ gridColumn: 'span 2' }}>
                        <CustomSearchableSelect
                            label="Assign Departments"
                            isMulti
                            options={departments.map(d => ({ label: d.name, value: d.id }))}
                            value={watch('department_ids') || []}
                            onChange={(val) => setValue('department_ids', val, { shouldValidate: true })}
                            placeholder="Search and select departments..."
                            leftIcon={<LayoutGrid size={18} />}
                            error={errors.department_ids?.message}
                            hideAvatar
                            required
                        />
                    </div>

                    <div style={{ gridColumn: 'span 2' }}>
                        <CustomInput label="Full Name" placeholder="Enter employee name" leftIcon={<User size={18} />} error={errors.name?.message} {...register('name')} required />
                    </div>

                    <div style={{ gridColumn: 'span 1' }}>
                        <CustomInput
                            label="Designation"
                            placeholder="e.g. Teacher, Coordinator"
                            leftIcon={<LayoutGrid size={18} />}
                            error={errors.designation?.message}
                            {...register('designation')}
                            required
                        />
                    </div>

                    <div style={{ gridColumn: 'span 1' }}>
                        <CustomInput
                            label="Phone Number"
                            placeholder="Enter phone number"
                            leftIcon={<Phone size={18} />}
                            error={errors.phone_number?.message}
                            {...register('phone_number')}
                            required
                        />
                    </div>

                    <div style={{ gridColumn: 'span 2' }}>
                        <CustomInput label="Email Address" type="email" placeholder="employee@school.com" leftIcon={<Mail size={18} />} error={errors.email?.message} {...register('email')} required />
                    </div>



                    <div style={{ gridColumn: 'span 2', marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
                        <CustomButton type="button" variant="outline" color="secondary" onClick={() => { setIsEditModalOpen(false); setEmployeeToEdit(null); reset(); }} disabled={isSubmitting}>
                            Cancel
                        </CustomButton>
                        <CustomButton type="submit" variant="filled" color="primary" loading={isSubmitting}>
                            Save Changes
                        </CustomButton>
                    </div>
                </form>
            </CustomModal>
            {importSummaryData && (
                <ImportSummaryModal
                    isOpen={isImportSummaryOpen}
                    onClose={() => {
                        setIsImportSummaryOpen(false);
                        setImportSummaryData(null);
                    }}
                    successCount={importSummaryData.success}
                    failedCount={importSummaryData.failed}
                    errors={importSummaryData.errors}
                    fileName={importSummaryData.fileName}
                    title="Employee Import Summary"
                />
            )}

            <ImportPreviewModal
                isOpen={isImportPreviewOpen}
                onClose={() => {
                    setIsImportPreviewOpen(false);
                    setImportFile(null);
                }}
                file={importFile}
                type="employees"
                onImport={(file) => employeeApi.importFile(schoolSlug, 'employees', file)}
                onSuccess={() => {
                    fetchEmployees();
                }}
            />
        </div>
    );
};
